import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { displayName } from "../lib/anagConfig";
import SegnalatoreSchedaDialog from "./SegnalatoreSchedaDialog";
import { Input } from "./ui/input";
import { Loader2, Pencil, Plus, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

// Campo Segnalatore con la stessa logica del wizard: autocomplete su anagrafica segnalatori,
// pulsante "Apri scheda" (matita) e "+ Crea nuovo segnalatore".
export default function SegnalatoreField({ value, onChange, testId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { editRec, initialName }
  const ref = useRef(null);
  const timer = useRef(null);
  // Bozza locale durante la digitazione: NON salva ad ogni carattere.
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commitDraft = () => { const v = (draft || "").trim(); if (v !== (value || "").trim()) onChange(v); };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const sortTok = (s) => norm(s).split(" ").filter(Boolean).sort().join(" ");

  const loadList = (q) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true); setOpen(true);
      try {
        const qq = (q || "").trim();
        const url = qq.length >= 2 ? `/anagrafiche/segnalatori?q=${encodeURIComponent(qq)}` : `/anagrafiche/segnalatori`;
        const { data } = await api.get(url);
        setItems(data || []);
      } catch { setItems([]); } finally { setLoading(false); }
    }, 250);
  };

  const findRec = (list, nome) => {
    const target = norm(nome), targetSorted = sortTok(nome);
    return (list || []).find((r) => {
      const dn = norm(displayName(r));
      const nc = norm([r.nome, r.cognome].filter(Boolean).join(" "));
      const cn = norm([r.cognome, r.nome].filter(Boolean).join(" "));
      const den = norm(r.denominazione);
      if (dn === target || nc === target || cn === target || (den && den === target)) return true;
      const rs = sortTok([r.nome, r.cognome].filter(Boolean).join(" "));
      return rs && rs === targetSorted;
    });
  };

  const openScheda = async () => {
    const nome = (draft || value || "").trim();
    if (!nome) return;
    try {
      const { data } = await api.get(`/anagrafiche/segnalatori`);
      const rec = findRec(data, nome);
      if (!rec) { toast.error("Scheda segnalatore non trovata in anagrafica"); return; }
      setDialog({ editRec: rec, initialName: nome });
    } catch { toast.error("Impossibile aprire la scheda segnalatore"); }
  };

  const noMatch = !!(draft && draft.trim().length >= 2 && !items.some((r) => norm(displayName(r)) === norm(draft)));

  return (
    <div className="mt-1 relative" ref={ref}>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Input value={draft} onChange={(e) => { setDraft(e.target.value); loadList(e.target.value); }} onFocus={() => loadList("")} onBlur={commitDraft}
            data-testid={testId} className="h-9 bg-slate-900/60 border-slate-700 pr-8" placeholder="Seleziona o cerca segnalatore…" autoComplete="off" />
          <button type="button" tabIndex={-1} onClick={() => (open ? setOpen(false) : loadList(""))} data-testid={`${testId}-toggle`}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"><ChevronsUpDown className="h-4 w-4" /></button>
          {open && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-56 overflow-y-auto" data-testid={`${testId}-list`}>
              {loading && <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</div>}
              {items.map((r) => (
                <button key={r.id} type="button" onMouseDown={(e) => { e.preventDefault(); setDraft(displayName(r)); onChange(displayName(r)); setOpen(false); setItems([]); }} data-testid={`${testId}-opt`}
                  className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60 border-b border-slate-800 last:border-0">{displayName(r)}</button>
              ))}
              {!loading && (
                <button type="button" onMouseDown={(e) => { e.preventDefault(); setDialog({ editRec: null, initialName: noMatch ? draft : "" }); setOpen(false); }} data-testid={`${testId}-crea`}
                  className="sticky bottom-0 w-full text-left px-3 py-2 text-sm font-medium text-teal-300 bg-[#121824] hover:bg-[#15302f] flex items-center gap-1.5 border-t border-slate-700"><Plus className="h-4 w-4 shrink-0" /> <span className="truncate">Crea nuovo segnalatore{noMatch ? <> “{draft.trim()}”</> : null}…</span></button>
              )}
            </div>
          )}
        </div>
        {draft && draft.trim() && (
          <button type="button" onClick={openScheda} data-testid={`${testId}-edit`} title="Apri scheda segnalatore"
            className="shrink-0 h-9 px-2 flex items-center gap-1 rounded-md border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 text-xs font-semibold"><Pencil className="h-3.5 w-3.5" /> Apri scheda</button>
        )}
      </div>
      {dialog && (
        <SegnalatoreSchedaDialog rec={dialog.editRec} initialName={dialog.initialName}
          onSaved={(rec) => { setDraft(displayName(rec)); onChange(displayName(rec)); setDialog(null); }} onCancel={() => setDialog(null)} />
      )}
    </div>
  );
}
